import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Heart, Layers, MessageCircle, User, Home } from "lucide-react";
import { api } from "../lib/api";

/**
 * Native-app bottom tab bar — Play · Rooms · ❤ (center create-room CTA) · Packs · Profile.
 *
 * Mounted on the dashboard, history (rooms), pricing (packs), profile. Hidden
 * during gameplay (Room.jsx) so the tab doesn't compete with the game card,
 * and on legal/marketing pages where it'd feel out of place.
 *
 * The center heart is the always-visible primary action: it scrolls back to
 * the top of the dashboard (where the create-room CTA lives) so the user can
 * start a new game in one tap from anywhere in the app.
 */
export default function BottomTabBar({ active }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();

  const isActive = (key) => {
    if (active) return active === key;
    if (key === "play" && (loc.pathname === "/" || loc.pathname.startsWith("/join"))) return true;
    if (key === "chat" && (loc.pathname.startsWith("/chat") || loc.pathname.startsWith("/whispers"))) return true;
    if (key === "packs" && loc.pathname.startsWith("/pricing")) return true;
    if (key === "profile" && (loc.pathname.startsWith("/login") || loc.pathname.startsWith("/questions") || loc.pathname.startsWith("/history"))) return true;
    return false;
  };

  // Lightweight unread polling for the Chat tab badge — counts unseen incoming
  // chat messages from the paired partner.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const fetchUnread = () => {
      api.chatThread()
        .then((d) => { if (alive) setUnread(d?.unread_count || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 20000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const handleHeartTap = () => {
    if (loc.pathname === "/") {
      // Already home → scroll to top so the create-room CTA is visible
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      nav("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
    }
  };

  return (
    <nav
      className="fixed left-1/2 -translate-x-1/2 z-50 w-[min(560px,calc(100vw-16px))]"
      style={{
        // Lifted above the Emergent watermark (which sits at bottom-right
        // ~16-48px) plus the iOS home indicator. 64px clears it cleanly.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)",
      }}
      data-testid="bottom-tab-bar"
    >
      <div
        className="relative flex items-end justify-around rounded-[22px] px-1.5 py-1 border border-white/10"
        style={{
          background:
            "linear-gradient(180deg, rgba(20, 12, 32, 0.82) 0%, rgba(11, 6, 20, 0.94) 100%)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          boxShadow:
            "0 14px 36px rgba(0, 0, 0, 0.4), 0 0 18px rgba(255, 79, 216, 0.07)",
        }}
      >
        <Tab
          to="/"
          label="Play"
          icon={<Layers className="w-[18px] h-[18px]" strokeWidth={2.2} />}
          active={isActive("play")}
          testid="tab-play"
        />
        <Tab
          to="/chat"
          label="Chat"
          icon={<MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />}
          active={isActive("chat")}
          testid="tab-chat"
          badge={unread}
        />
        {/* Center action — heart (start a room from anywhere) */}
        <button
          onClick={handleHeartTap}
          aria-label="Start a new vibe"
          data-testid="tab-heart-cta"
          className="relative -mt-5 flex flex-col items-center group active:scale-95 transition-transform"
        >
          <span
            className="w-[50px] h-[50px] rounded-full flex items-center justify-center"
              style={{
              background:
                "radial-gradient(circle at 30% 30%, #ff4fd8 0%, #ff4d8d 55%, #7B61FF 100%)",
              boxShadow:
                "0 0 0 3px rgba(11, 6, 20, 0.95), 0 0 18px rgba(255, 79, 216, 0.5), 0 0 38px rgba(255, 79, 216, 0.28)",
            }}
          >
            <Heart
              className="w-[22px] h-[22px] text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)]"
              fill="white"
              strokeWidth={1.5}
            />
          </span>
        </button>
        <Tab
          to="/pricing"
          label="Packs"
          icon={<Home className="w-[18px] h-[18px]" strokeWidth={2.2} />}
          active={isActive("packs")}
          testid="tab-packs"
        />
        <Tab
          to={user ? "/questions" : "/login"}
          label="Profile"
          icon={<User className="w-[18px] h-[18px]" strokeWidth={2.2} />}
          active={isActive("profile")}
          testid="tab-profile"
        />
      </div>
    </nav>
  );
}

function Tab({ to, label, icon, active, testid, badge = 0 }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative flex flex-col items-center gap-0 px-3 pt-1 pb-0.5 min-w-[50px] transition-colors duration-200 ${
        active ? "text-[#ff4fd8]" : "text-[#9B98AE] hover:text-white"
      }`}
      style={
        active
          ? { filter: "drop-shadow(0 0 8px rgba(255, 79, 216, 0.55))" }
          : undefined
      }
    >
      <span className="relative">
        {icon}
        {badge > 0 && (
          <span
            data-testid={`${testid}-badge`}
            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF4D8D] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_8px_rgba(255,77,141,0.7)]"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span className="text-[9px] font-semibold tracking-[0.12em] uppercase mt-0.5">
        {label}
      </span>
    </Link>
  );
}
